import { ObjectId } from '@neighbourhood-munchies/shared-types';
import React, { createContext, useContext, useEffect, useReducer, useRef, useState } from 'react';
import { zoneMappingService } from '../services/foodOrderingApi';
import { CartActions, CartItem, CartState, Restaurant } from '../types';
import { useDeliveryZone } from './DeliveryZoneContext';

// ============================================================================
// CART CONTEXT TYPES
// ============================================================================

interface CartContextType extends CartState {
  actions: CartActions;
  canCheckout: boolean;
  isValidForRestaurant: (restaurantId: ObjectId) => boolean;
  isLoading: boolean;
  operationType: string | null;
}

// ============================================================================
// CART PERSISTENCE TYPES
// ============================================================================

interface PersistedCartData extends CartState {
  savedZoneId?: string; // Zone ID when cart was saved
  savedTimestamp?: number; // When cart was saved
  version?: string; // Cart data version for migration
}

// ============================================================================
// CART REDUCER TYPES
// ============================================================================

type CartAction =
  | { type: 'ADD_ITEM'; payload: CartItem }
  | { type: 'UPDATE_QUANTITY'; payload: { itemId: string; quantity: number } }
  | { type: 'REMOVE_ITEM'; payload: { itemId: string } }
  | { type: 'CLEAR_CART' }
  | { type: 'SET_RESTAURANT'; payload: Restaurant }
  | { type: 'UPDATE_DELIVERY_FEE'; payload: { deliveryFee: number } }
  | { type: 'SET_DELIVERY_FEE_CALCULATING' }
  | { type: 'SET_DELIVERY_FEE_SUCCESS'; payload: { deliveryFee: number; source: string } }
  | { type: 'SET_DELIVERY_FEE_ERROR'; payload: { error: string; fallbackFee?: number } }
  | { type: 'LOAD_CART'; payload: CartState }
  | { type: 'LOAD_CART_PRESERVE_STATUS'; payload: CartState };

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: CartState = {
  items: [],
  restaurant: null,
  total: 0,
  itemCount: 0,
  deliveryFee: 0,
  deliveryFeeCalculationStatus: 'idle',
  deliveryFeeError: undefined,
};

// ============================================================================
// CART REDUCER
// ============================================================================

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const newItem = action.payload;
      const existingItemIndex = state.items.findIndex(
        (item) =>
          item.menuItemId === newItem.menuItemId &&
          JSON.stringify(item.customizations) === JSON.stringify(newItem.customizations)
      );

      let updatedItems: CartItem[];

      if (existingItemIndex >= 0) {
        // Update quantity of existing item
        updatedItems = state.items.map((item, index) =>
          index === existingItemIndex
            ? {
                ...item,
                quantity: item.quantity + newItem.quantity,
                totalPrice: item.totalPrice + newItem.totalPrice,
              }
            : item
        );
      } else {
        // Add new item
        updatedItems = [...state.items, newItem];
      }

      const newTotal = updatedItems.reduce((sum, item) => sum + item.totalPrice, 0);
      const newItemCount = updatedItems.reduce((sum, item) => sum + item.quantity, 0);

      return {
        ...state,
        items: updatedItems,
        total: newTotal,
        itemCount: newItemCount,
      };
    }

    case 'UPDATE_QUANTITY': {
      const { itemId, quantity } = action.payload;

      if (quantity <= 0) {
        // Remove item if quantity is 0 or negative
        return cartReducer(state, { type: 'REMOVE_ITEM', payload: { itemId } });
      }

      const updatedItems = state.items.map((item) =>
        item.id === itemId ? { ...item, quantity, totalPrice: (item.totalPrice / item.quantity) * quantity } : item
      );

      const newTotal = updatedItems.reduce((sum, item) => sum + item.totalPrice, 0);
      const newItemCount = updatedItems.reduce((sum, item) => sum + item.quantity, 0);

      return {
        ...state,
        items: updatedItems,
        total: newTotal,
        itemCount: newItemCount,
      };
    }

    case 'REMOVE_ITEM': {
      const { itemId } = action.payload;
      const updatedItems = state.items.filter((item) => item.id !== itemId);

      const newTotal = updatedItems.reduce((sum, item) => sum + item.totalPrice, 0);
      const newItemCount = updatedItems.reduce((sum, item) => sum + item.quantity, 0);

      return {
        ...state,
        items: updatedItems,
        total: newTotal,
        itemCount: newItemCount,
        restaurant: updatedItems.length === 0 ? null : state.restaurant, // Clear restaurant if cart becomes empty
      };
    }

    case 'CLEAR_CART':
      return {
        ...initialState,
        restaurant: null, // Clear restaurant when cart is cleared
        deliveryFeeCalculationStatus: 'idle', // Reset to trigger recalculation
        deliveryFeeError: undefined,
      };

    case 'SET_RESTAURANT': {
      const restaurant = action.payload;
      return {
        ...state,
        restaurant,
        // Clear cart if switching to a different restaurant
        items: state.restaurant && state.restaurant.id !== restaurant.id ? [] : state.items,
        total: state.restaurant && state.restaurant.id !== restaurant.id ? 0 : state.total,
        itemCount: state.restaurant && state.restaurant.id !== restaurant.id ? 0 : state.itemCount,
      };
    }

    case 'UPDATE_DELIVERY_FEE': {
      return {
        ...state,
        deliveryFee: action.payload.deliveryFee,
      };
    }

    case 'SET_DELIVERY_FEE_CALCULATING': {
      return {
        ...state,
        deliveryFeeCalculationStatus: 'calculating',
        deliveryFeeError: undefined,
      };
    }

    case 'SET_DELIVERY_FEE_SUCCESS': {
      return {
        ...state,
        deliveryFee: action.payload.deliveryFee,
        deliveryFeeCalculationStatus: 'success',
        deliveryFeeError: undefined,
      };
    }

    case 'SET_DELIVERY_FEE_ERROR': {
      return {
        ...state,
        deliveryFeeCalculationStatus: 'error',
        deliveryFeeError: action.payload.error,
        // Use fallback fee if provided, otherwise keep current fee
        ...(action.payload.fallbackFee !== undefined && {
          deliveryFee: action.payload.fallbackFee,
        }),
      };
    }

    case 'LOAD_CART':
      return {
        ...action.payload,
        // Reset delivery fee calculation status when loading from storage
        deliveryFeeCalculationStatus: 'idle',
        deliveryFeeError: undefined,
      };

    case 'LOAD_CART_PRESERVE_STATUS':
      return {
        ...action.payload,
        // Preserve the existing delivery fee calculation status
      };

    default:
      return state;
  }
};

// ============================================================================
// CART CONTEXT
// ============================================================================

export const CartContext = createContext<CartContextType | undefined>(undefined);

// ============================================================================
// CART PROVIDER
// ============================================================================

interface CartProviderProps {
  children: React.ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const [isLoading, setIsLoading] = useState(false);
  const [operationType, setOperationType] = useState<string | null>(null);
  const [cartLoaded, setCartLoaded] = useState(false);
  const { currentZone } = useDeliveryZone();

  // Refs to track previous values and prevent unnecessary recalculations
  const prevZoneIdRef = useRef<string | undefined>(undefined);
  const prevRestaurantIdRef = useRef<ObjectId | undefined>(undefined);
  const isCalculatingRef = useRef<boolean>(false);

  // Load cart from localStorage on mount and when currentZone becomes available
  useEffect(() => {
    if (currentZone && !cartLoaded) {
      loadCartFromStorage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentZone, cartLoaded]);

  const loadCartFromStorage = async () => {
    if (!currentZone) return;

    // Requirements 1.3, 1.4: Load cart from browser storage on initialization
    // Try localStorage first, then sessionStorage as fallback
    let savedCart = localStorage.getItem('neighbourhood-munchies-cart');
    let storageType: 'localStorage' | 'sessionStorage' = 'localStorage';

    if (!savedCart) {
      savedCart = sessionStorage.getItem('neighbourhood-munchies-cart');
      storageType = 'sessionStorage';
    }

    if (!savedCart) {
      setCartLoaded(true);
      return;
    }

    try {
      const parsedCart: PersistedCartData = JSON.parse(savedCart);

      // Check if zone has changed since cart was saved
      const hasZoneChanged = parsedCart.savedZoneId && parsedCart.savedZoneId !== currentZone.id;

      // Check if cart is stale (older than 24 hours)
      const isStale =
        parsedCart.savedTimestamp && Date.now() - parsedCart.savedTimestamp > 24 * 60 * 60 * 1000;

      if (hasZoneChanged || isStale) {
        console.log('Cart zone changed or stale, recalculating delivery fees', {
          hasZoneChanged,
          isStale,
          savedZoneId: parsedCart.savedZoneId,
          currentZoneId: currentZone.id,
        });

        // Load cart and reset calculation status for recalculation
        dispatch({ type: 'LOAD_CART', payload: parsedCart });

        // Force recalculation of delivery fees
        if (parsedCart.restaurant) {
          await recalculateDeliveryFeeForLoadedCart(parsedCart.restaurant);
        }
      } else {
        // Cart is fresh and zone hasn't changed, preserve the calculation status
        console.log('Cart is fresh and zone unchanged, preserving delivery fee calculation status');
        dispatch({ type: 'LOAD_CART_PRESERVE_STATUS', payload: parsedCart });
      }

      setCartLoaded(true);
    } catch (error) {
      console.error(`Failed to load cart from ${storageType}:`, error);
      // Clean up corrupted data from both storage types
      localStorage.removeItem('neighbourhood-munchies-cart');
      sessionStorage.removeItem('neighbourhood-munchies-cart');
      setCartLoaded(true); // Mark as loaded even on error to prevent retry loops
    }
  };

  const recalculateDeliveryFeeForLoadedCart = async (restaurant: Restaurant) => {
    if (!currentZone || isCalculatingRef.current) return;

    try {
      isCalculatingRef.current = true;
      dispatch({ type: 'SET_DELIVERY_FEE_CALCULATING' });

      if (!restaurant.zone_id) {
        console.warn('Restaurant does not have a zone_id, using zone default delivery cost');
        dispatch({
          type: 'SET_DELIVERY_FEE_SUCCESS',
          payload: {
            deliveryFee: currentZone.delivery_cost,
            source: 'zone_default',
          },
        });
        prevZoneIdRef.current = currentZone.id;
        prevRestaurantIdRef.current = restaurant.id;
        return;
      }

      const result = await zoneMappingService.calculateDeliveryFee(
        restaurant.zone_id,
        currentZone.id
      );

      dispatch({
        type: 'SET_DELIVERY_FEE_SUCCESS',
        payload: {
          deliveryFee: result.deliveryFee,
          source: result.source,
        },
      });

      prevZoneIdRef.current = currentZone.id;
      prevRestaurantIdRef.current = restaurant.id;

      console.log(
        `Delivery fee recalculated for loaded cart: ${result.deliveryFee} (source: ${result.source})`
      );
    } catch (error) {
      console.error('Failed to recalculate delivery fee for loaded cart:', error);

      dispatch({
        type: 'SET_DELIVERY_FEE_ERROR',
        payload: {
          error: 'Unable to calculate delivery fee. Using default rate.',
          fallbackFee: currentZone.delivery_cost,
        },
      });
    } finally {
      isCalculatingRef.current = false;
    }
  };

  // CONSOLIDATED: Single effect to handle all delivery fee calculations
  // This replaces 4 separate useEffect hooks that were causing cascading updates
  useEffect(() => {
    // Don't do anything until cart is loaded
    if (!cartLoaded || !currentZone) return;

    const zoneChanged = prevZoneIdRef.current !== currentZone.id;
    const restaurantChanged = prevRestaurantIdRef.current !== state.restaurant?.id;
    const needsCalculation = state.deliveryFeeCalculationStatus === 'idle';
    const hasRestaurant = !!state.restaurant;

    // Case 1: No restaurant selected - use zone default
    if (!hasRestaurant) {
      if (zoneChanged) {
        dispatch({
          type: 'UPDATE_DELIVERY_FEE',
          payload: { deliveryFee: currentZone.delivery_cost },
        });
        prevZoneIdRef.current = currentZone.id;
      }
      return;
    }

    // Case 2: Restaurant selected - calculate delivery fee only if needed
    // Trigger calculation if:
    // - Zone changed
    // - Restaurant changed
    // - Status is idle (cart cleared, loaded from storage, etc.)
    if ((zoneChanged || restaurantChanged || needsCalculation) && !isCalculatingRef.current) {
      recalculateDeliveryFee();
    }
  }, [cartLoaded, currentZone, state.restaurant, state.deliveryFeeCalculationStatus]);

  // Save cart to localStorage whenever state changes (debounced via React's batching)
  useEffect(() => {
    if (!cartLoaded) return; // Don't save until initial load is complete

    saveCartToStorage();
  }, [state, currentZone, cartLoaded]);

  const saveCartToStorage = () => {
    try {
      const persistedData: PersistedCartData = {
        ...state,
        savedZoneId: currentZone?.id,
        savedTimestamp: Date.now(),
        version: '1.0',
      };

      const serializedData = JSON.stringify(persistedData);

      try {
        // Requirements 1.3, 1.4: Persist cart state in browser storage
        localStorage.setItem('neighbourhood-munchies-cart', serializedData);
      } catch (storageError) {
        // Handle localStorage quota exceeded errors
        if (
          storageError instanceof DOMException &&
          (storageError.name === 'QuotaExceededError' ||
            storageError.name === 'NS_ERROR_DOM_QUOTA_REACHED')
        ) {
          console.warn('localStorage quota exceeded, falling back to sessionStorage');

          try {
            // Fallback to sessionStorage
            sessionStorage.setItem('neighbourhood-munchies-cart', serializedData);
          } catch (sessionError) {
            console.warn(
              'sessionStorage also failed, cart will only persist in memory:',
              sessionError
            );
            // Cart will still work in memory, just won't persist across sessions
          }
        } else {
          throw storageError;
        }
      }
    } catch (error) {
      console.warn('Failed to save cart to storage:', error);
      // Continue silently - cart will still work without persistence
    }
  };

  // Helper function to execute operations with loading state
  const executeWithLoading = async (type: string, operation: () => void | Promise<void>) => {
    try {
      setIsLoading(true);
      setOperationType(type);
      await operation();
    } finally {
      setIsLoading(false);
      setOperationType(null);
    }
  };

  // Delivery fee calculation method
  const recalculateDeliveryFee = async (): Promise<void> => {
    if (!currentZone || !state.restaurant || isCalculatingRef.current) {
      return;
    }

    // Check if restaurant has a zone_id
    if (!state.restaurant.zone_id) {
      console.warn('Restaurant does not have a zone_id, using zone default delivery cost');
      dispatch({
        type: 'SET_DELIVERY_FEE_SUCCESS',
        payload: {
          deliveryFee: currentZone.delivery_cost,
          source: 'zone_default',
        },
      });
      prevZoneIdRef.current = currentZone.id;
      prevRestaurantIdRef.current = state.restaurant.id;
      return;
    }

    try {
      isCalculatingRef.current = true;
      dispatch({ type: 'SET_DELIVERY_FEE_CALCULATING' });

      const result = await zoneMappingService.calculateDeliveryFee(
        state.restaurant.zone_id,
        currentZone.id
      );

      dispatch({
        type: 'SET_DELIVERY_FEE_SUCCESS',
        payload: {
          deliveryFee: result.deliveryFee,
          source: result.source,
        },
      });

      // Update refs to track what we just calculated
      prevZoneIdRef.current = currentZone.id;
      prevRestaurantIdRef.current = state.restaurant.id;

      if (result.error) {
        console.warn('Delivery fee calculation warning:', result.error);
      }
    } catch (error) {
      console.error('Delivery fee calculation failed completely:', error);

      // Determine appropriate fallback and error message
      let fallbackFee = currentZone.delivery_cost;
      let errorMessage = 'Unable to calculate delivery fee. Using standard rate for your area.';

      // Check if it's a network-related error
      if (error instanceof Error) {
        const message = error.message.toLowerCase();
        if (
          message.includes('network') ||
          message.includes('timeout') ||
          message.includes('connection') ||
          message.includes('unavailable')
        ) {
          errorMessage = 'Connection issue detected. Using standard delivery rate.';
        } else if (message.includes('not found')) {
          errorMessage = 'Delivery information not available. Using standard rate.';
        }
      }

      dispatch({
        type: 'SET_DELIVERY_FEE_ERROR',
        payload: {
          error: errorMessage,
          fallbackFee,
        },
      });
    } finally {
      isCalculatingRef.current = false;
    }
  };

  // Cart actions
  const actions: CartActions = {
    addItem: (item: CartItem) => {
      executeWithLoading('add', () => {
        dispatch({ type: 'ADD_ITEM', payload: item });
      });
    },

    updateQuantity: (itemId: string, quantity: number) => {
      executeWithLoading('update', () => {
        dispatch({ type: 'UPDATE_QUANTITY', payload: { itemId, quantity } });
      });
    },

    removeItem: (itemId: string) => {
      executeWithLoading('remove', () => {
        dispatch({ type: 'REMOVE_ITEM', payload: { itemId } });
      });
    },

    clearCart: () => {
      executeWithLoading('clear', () => {
        dispatch({ type: 'CLEAR_CART' });
      });
    },

    setRestaurant: (restaurant: Restaurant) => {
      dispatch({ type: 'SET_RESTAURANT', payload: restaurant });
    },

    recalculateDeliveryFee,
  };

  // Computed properties
  const canCheckout = state.items.length > 0 && state.restaurant !== null;

  const isValidForRestaurant = (restaurantId: ObjectId): boolean => {
    return state.restaurant === null || state.restaurant.id === restaurantId;
  };

  const contextValue: CartContextType = {
    ...state,
    actions,
    canCheckout,
    isValidForRestaurant,
    isLoading,
    operationType,
  };

  return <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>;
};

// ============================================================================
// CART HOOK
// ============================================================================

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
